/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import './styles.scss';
import axios from 'axios';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQuery } from '../../../../../Hooks/API/useQuery';
import { errorSignal } from '../../../../../Signals/errorSignal';
import { isFormLoading, nextButtonActive } from '../../../../../Signals/formStateSignal';
import { useQueryParamsStore } from '../../../../../Hooks/Zustand/useQueryParamsStore';
import { useCheckoutStore } from '../../../../../Hooks/Zustand/useCheckoutStore';

/**
 * Custom CreditCardCheckout hook
 *
 * This hook handles the complete credit card payment process.
 * The reason we use axios and not the middleware is that the origin we use to load the adyen form component,
 * must be the same as the origin we use to send the data to magicline
 *
 * You can find the documentation here: https://developer.magicline.com/apis/connectapi/workflow-contract/#adyen-webcomponent
 *
 * @returns {{isLoading, rateBundleData}}
 */
export function useCreditCardCheckout() {
  const { values, setFieldValue } = useFormikContext();

  const { getQueryParameter } = useQueryParamsStore();
  const {
    setFormData, getFormDataValue, getFormData, updateFormDataValue,
  } = useCheckoutStore();
  const [adyenPaymentState, setAdyenPaymentState] = useState(
    getFormDataValue('adyenPaymentState') ? getFormDataValue('adyenPaymentState') : 'INITIALIZED',
  );
  const isPaymentRedirect = getFormDataValue('adyenPaymentState') === 'PAYMENT_REDIRECT';
  const paymentInitiatedAfterRedirect = useRef(false);
  const tokenizationReference = useRef('');
  const { t } = useTranslation();
  const { data: paymentMethodData, isLoading } = useQuery('paymentMethods', {
    locale: 'de-DE',
    countryCode: 'DE',
    studioId: values.studioId,
  });

  /**
     * This function is called whenever an error occurs in the payment process.
     * When something went wrong we update the payment state and display an error message for the user
     * @param err
     */
  const onPaymentError = useCallback(
    (err) => {
      // eslint-disable-next-line no-console
      console.error(err);
      // Display generic error message
      errorSignal.signal.value = t('ERROR_MESSAGE_CREDIT_CARD_PAYMENT');
      // Set to error state, so we can display an error message for the user
      setAdyenPaymentState('PAYMENT_ERROR');
      // Set loading status to false
      isFormLoading.value = false;
    },
    [t],
  );

  /**
     * ( 5 ): This function is called when the payment process is completed, and we sent all the data to magicline
     *
     * While you also receive a result of the tokenization when calling the complete endpoint, it may happen that the tokenization isn't completely
     * finished in that very moment. To check the outcome of the tokenization process you need to regularly call the state endpoint
     *
     * Case "COMPLETE": The payment is completed. We update the formik context / store and the user can navigate to the next page
     * Case "INITIATE": The tokenization is not finished. We do it as described in the documentation to re-fetch the endpoint every 2 seconds
     * Case "FAILURE": Something went wrong and the payment is cancelled. We display a generic error message in the frontend.
     *
     */
  const onTokenizationState = useCallback(() => {
    axios
      .get(
        `https://fitone.api.magicline.com/connect/v1/creditcard/tokenization/${tokenizationReference.current}/state`,
      )
      .then((stateResult) => {
        switch (stateResult.data.state) {
          case 'COMPLETE':
            setAdyenPaymentState('PAYMENT_COMPLETE');
            setFieldValue('accountOwner', stateResult.data?.paymentInstrumentDto.creditCard.cardHolder);
            updateFormDataValue(
              'accountOwner',
              stateResult.data?.paymentInstrumentDto.creditCard.cardHolder,
            );
            setFieldValue('cardNumber', stateResult.data?.paymentInstrumentDto.creditCard.cardNumber);
            updateFormDataValue('cardNumber', stateResult.data?.paymentInstrumentDto.creditCard.cardNumber);
            setFieldValue('adyenPaymentState', 'PAYMENT_COMPLETE');
            updateFormDataValue('adyenPaymentState', 'PAYMENT_COMPLETE');
            nextButtonActive.value = true;
            isFormLoading.value = false;
            break;
          case 'INITIATE':
            setTimeout(() => {
              onTokenizationState();
            }, 2000);
            break;
          case 'FAILURE':
          default:
            onPaymentError(stateResult);
            break;
        }
      })
      .catch((err) => onPaymentError(err));
  }, [onPaymentError, setFieldValue, updateFormDataValue]);

  /**
     * ( 4 ): This function is called when the initiate tokenization was successfully
     * and the user verified his credit card (for example: inApp Authentication)
     *
     * Depending on the authentication:
     * fill threeDSResult (authentication took place on your site)
     * or fill redirectResult (authentication via redirect)
     *
     * @param data
     * @return Promise
     */
  const onTokenizationComplete = useCallback(
    (data) => {
      const postData = {
        threeDSResult: data?.data?.details?.threeDSResult,
        redirectResult: getQueryParameter('tokenizationReference'),
      };
      return axios.post(
        `https://fitone.api.magicline.com/connect/v1/creditcard/tokenization/${tokenizationReference.current}/complete`,
        postData,
      );
    },
    [getQueryParameter],
  );

  /**
     * ( 3 ): This function is called when the initiate tokenization request (1) was successfully and the response does not contain an error (2)
     * In this case we need to save the tokenizationReference which is an uuid, and check is the user need additional verification.
     *
     * Type 1 (Action: RedirectShopper):
     * The given responseAction is a redirect. So we need to store the current form state and then call the handleAction()
     * which redirects the user to validate his bank account. After that, the user gets redirected back from validating his bank account
     * and the redirectUrl does contain the tokenizationReference.
     *
     * Type 2 (Action: IdentifyShopper / ChallengerShopper):
     * The given responseAction is not a redirect. We can skip the store process and let the adyenFormComponent handle the action which results
     * in an onPage verification
     *
     * See here for more information: https://docs.adyen.com/online-payments/build-your-integration/payment-result-codes/#3d-secure-authentication
     *
     * Special: The response does not always contain an action.
     * This means that the user has already authenticated the card, and it's saved in the magicline backend.
     * This could happen when the card is used for multiple checkouts
     *
     * @param responseData Magicline response data from the tokenization initiate endpoint
     * @param adyenFormComponent Adyen form component reference
     */
  const onTokenizationSuccess = (responseData, adyenFormComponent) => {
    tokenizationReference.current = responseData.reference;
    setFieldValue('tokenizationReference', responseData.reference);
    if (responseData.action) {
      const responseAction = JSON.parse(responseData.action);
      // Check for Type 1 or Type 2 action
      if (responseAction.type === 'redirect') {
        // Set to formik context and update the store
        setFieldValue('adyenPaymentState', 'PAYMENT_REDIRECT').then(() => {
          setFormData({ ...getFormData(), ...values });
        });
      }
      adyenFormComponent.handleAction(responseAction);
    } else {
      // When no action is set in the response, the user has already authenticated the card, and it's saved in the magicline backend
      onTokenizationState();
    }
  };

  /**
     * ( 2 ): This function is called after initiateTokenization() is called.
     * The returned magicline response does always contain the status code 200, even if the payment is not accepted
     * So we need to check if the result contains an error, so we can handle it if required
     *
     * See https://docs.adyen.com/online-payments/build-your-integration/payment-result-codes/
     *
     * @param responseData
     * @returns {boolean}
     */
  const checkIfResponseIsValid = (responseData) => {
    if (
      responseData.resultCode === 'Cancelled'
            || responseData.resultCode === 'Error'
            || responseData.resultCode === 'Refused'
    ) {
      onPaymentError(responseData);
      return false;
    }
    return true;
  };

  /**
     * ( 1 ): First function which is called after the user submits the credits card data in the adyen form component to initiate the tokenization
     * We send the tokenized data, retrieved from adyen, to magicline.
     *
     * @param state
     * @return Promise
     */
  const initiateTokenization = (state) => {
    // Data for the tokenization initiation
    const postData = {
      paymentMethod: JSON.stringify(state.data.paymentMethod),
      browserInfo: JSON.stringify(state.data.browserInfo),
      studioId: values.studioId,
      returnUrl: window.location.href,
      origin: state.data.origin,
    };
    // Send the data to magicline
    return axios.post('https://fitone.api.magicline.com/connect/v2/creditcard/tokenization/initiate', postData);
  };

  /**
     * This useEffect hook is only executed when the user is redirected back to the checkout after he validated his bank account.
     * If it's a redirect, the adyen checkout form does not execute the onAdditionalDetails callback. So we need to do the logic manually.
     */
  useEffect(() => {
    if (isPaymentRedirect && !paymentInitiatedAfterRedirect.current) {
      paymentInitiatedAfterRedirect.current = true;
      onTokenizationComplete(null)
        .then(() => {
          onTokenizationState();
        })
        .catch((err) => onPaymentError(err));
    }
  }, [isPaymentRedirect, onPaymentError, onTokenizationComplete, onTokenizationState]);

  /**
     * This function returns the base adyen form configuration.
     * The data contains the available payments response from magicline and some additional configurations which is needed to load the form.
     */
  const getClientBaseConfiguration = () => ({
    paymentMethodsResponse: JSON.parse(paymentMethodData.paymentMethodsJson),
    clientKey: 'live_A4L6Z7SMZRFSZCOWD56DA3Q6GYFXB4MX',
    environment: 'live',
    locale: 'de_DE',
    showPayButton: true,
    paymentMethodsConfiguration: {
      ideal: {
        showImage: true,
      },
      card: {
        hasHolderName: true,
        holderNameRequired: true,
        name: 'Kredit- oder Debitkarte',
        amount: {
          value: 0,
          currency: 'EUR',
        },
        styles: {
          base: {},
          placeholder: {},
          error: {
            color: '#001b2b',
          },
        },
      },
    },
  });

  /**
     * Get the client callback configuration
     *
     * onSubmit: Is executed when the user enters his credit card data and submits the adyen payment form
     * onAdditionalDetails: Is executed when the user authenticated his credit card on the current site (3DS2)
     * onError: Is executed when something went wrong
     *
     */
  const getClientMethodsConfiguration = () => ({
    /**
         * Before ( 1 ): The onSubmit function is executed when the user enters his credit card data and submits the adyen payment form
         * @param state
         * @param component
         */
    onSubmit: (state, component) => {
      if (state.isValid) {
        initiateTokenization(state)
          .then((res) => {
            /**
                         * Due to the reason the response does always have an 200 status code we need to check if
                         * the payment is valid and display an error message when something is wrong
                         * @type {boolean}
                         */
            const responseIsValid = checkIfResponseIsValid(res.data);
            /**
                         * When the response is valid and does not contain any error messages whe need to send the given data back to adyen
                         */
            if (responseIsValid) {
              onTokenizationSuccess(res.data, component);
            }
          })
          .catch((err) => onPaymentError(err));
      }
    },
    /**
         * After ( 3 ): The onAdditionalDetails function is executed when the user authenticated his credit card on the current site (3DS2).
         * The WebComponent will invoke this callback and passing an object that contains the result
         * @param data
         */
    onAdditionalDetails: (data) => {
      isFormLoading.value = true;
      onTokenizationComplete(data)
        .then(() => {
          onTokenizationState();
        })
        .catch((err) => onPaymentError(err));
    },
    /**
         * This function is executed when something went wrong on the adyen backend servers.
         * It's not executed when something went wrong while we send the data to magicline.
         * @param err
         */
    onError: (err) => {
      onPaymentError(err);
    },
  });

  /**
     * Get Adyen Client Configuration
     * The configurations contains the base configuration which is needed to apply additional styling, form settings and keys.
     * The second configuration contains the form callbacks
     */
  const getClientConfiguration = () => ({
    ...getClientBaseConfiguration(),
    ...getClientMethodsConfiguration(),
  });

  return {
    isLoading,
    isPaymentRedirect,
    adyenPaymentState,
    getClientConfiguration,
  };
}
